import React, { useState, useEffect } from "react";
import {
  getUserProfile,
  getUserProfileImage,
  updateUserProfile,
  updateUserProfileImage,
} from "../../api/UserProfileApi";
import UserProfilePageForm from "./UserProfilePageForm";
import { Form, message } from "antd";
import moment from "moment";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import {
  appTitle,
  dateFormat,
  passwordRegex,
} from "../common/GeneralConfigurations";
import { useNavigate } from "react-router-dom";
import { addPassword } from "../../api/UserApi";

const ManageUserProfilePage = () => {
  const [visibleCP, setVisibleCP] = useState(false);
  const [image, setImage] = useState(null);
  const [form] = Form.useForm();
  const [formPassword] = Form.useForm();
  const [defaultPickerValue, setDefaultPickerValue] = useState(
    moment(`${moment().year() - 18}-${moment().month() + 1}-${moment().date()}`)
  );
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitPasswordLoading, setSubmitPasswordLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [formHasErrors, setFormHasErrors] = useState(false);
  const navigate = useNavigate();
  const samePasswords = "New password cannot be the same as your old password";
  const matchingPasswords = "Passwords do not match";
  const handleBack = () => navigate(-1);
  const title = "User Profile";
  useEffect(() => {
    document.title = `${appTitle} - ${title}`;
  }, []);
  const disabledDate = (current) => {
    if (
      moment().year() - current.year() < 18 ||
      moment().year() - current.year() > 120
    )
      return true;
    if (moment().year() - current.year() === 18) {
      if (moment().month() < current.month()) return true;
      if (moment().month() === current.month()) {
        if (moment().date() < current.date()) return true;
      }
    }
    return false;
  };

  const handleSubmit = (values) => {
    setSubmitLoading(true);
    updateUserProfile(values).then((res) => {
      setSubmitLoading(false);
      if (res.status === 200) {
        AdnMessageSuccess();
      } else {
        if (res.status === 400) {
          form.setFields(
            res.data.errors.map((e) => ({
              name: e.message.split(" ")[0].toLowerCase(),
              errors: [e.message],
            }))
          );
          return;
        }
        AdnMessageError();
      }
    });
  };
  const checkSamePasswords = () => {
    const value = formPassword.getFieldValue("newPassword");
    if (!value || !passwordRegex.test(value)) return;
    value === formPassword.getFieldValue("oldPassword")
      ? formPassword.setFields([
          {
            name: "newPassword",
            errors: [samePasswords],
          },
        ])
      : formPassword.setFields([{ name: "newPassword", errors: [] }]);
  };
  const checkMatchingPasswords = () => {
    const value = formPassword.getFieldValue("confirmPassword");
    if (!value) return;
    value !== formPassword.getFieldValue("newPassword")
      ? formPassword.setFields([
          {
            name: "confirmPassword",
            errors: [matchingPasswords],
          },
        ])
      : formPassword.setFields([{ name: "confirmPassword", errors: [] }]);
  };

  const handleSubmitPassword = (values) => {
    setSubmitPasswordLoading(true);
    addPassword(values)
      .then((res) => {
        setSubmitPasswordLoading(false);
        if (res.status === 200) AdnMessageSuccess();
        else {
          if (res.status === 400) {
            formPassword.setFields(
              res.data.errors.map((e) => ({
                name: e.code === "InvalidOldPassword" ? "oldPassword" : "",
                errors: [e.message],
              }))
            );
            return;
          }
          AdnMessageError();
        }
        setVisibleCP(false);
      })
      .catch((err) => console.log(err));
  };
  useEffect(() => {
    const fetchProf = getUserProfile().then((res) => {
      const {
        name,
        email,
        birthDate,
        phone,
        roleName,
        departmentName,
        workPlaceName,
        seniorManagerName,
      } = res.data;
      const bDate =
        birthDate === null
          ? null
          : moment(JSON.stringify(birthDate).substring(1, 11), dateFormat);
      form.setFieldsValue({
        name: name,
        email: email,
        birthDate: bDate,
        phone: phone,
        roleName: roleName,
        department: departmentName,
        workPlace: workPlaceName,
        seniorManager: seniorManagerName,
      });
      bDate && setDefaultPickerValue(bDate);
    });
    const fetchImage = getUserProfileImage().then((res) => {
      setImage(res.data.image);
    });
    Promise.all([fetchProf, fetchImage])
      .then(() => setLoading(false))
      .catch((err) => console.log(err));
  }, [form]);

  function getBase64(img, callback) {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  const saveImage = async (img) => {
    setImgLoading(true);
    let myFormData = new FormData();
    myFormData.append("Image", img);
    const res = await updateUserProfileImage(myFormData);
    setImgLoading(false);
    if (res.status !== 200) {
      message.error("Fail to upload photo");
      return;
    }
    getBase64(img, setImage);
  };

  const handleChangePasswordButton = (value) => {
    setVisibleCP(value);
    formPassword.resetFields();
  };
  const handleBlurName = () => {
    form.setFieldsValue({
      name: form
        .getFieldValue("name")
        ?.trim()
        .split(/\s+/)
        .filter((w) => w !== "")
        .map(
          ([firstChar, ...rest]) =>
            firstChar.toUpperCase() + rest.join("").toLowerCase()
        )
        .join(" "),
    });
    form
      .validateFields(["name"])
      .then(() => setFormHasErrors(false))
      .catch((f) => setFormHasErrors(f.errorFields.length > 0));
  };

  return (
    <>
      <UserProfilePageForm
        handleChangePasswordButton={handleChangePasswordButton}
        visibleCP={visibleCP}
        handleSubmit={handleSubmit}
        submitLoading={submitLoading}
        form={form}
        formPassword={formPassword}
        handleSubmitPassword={handleSubmitPassword}
        submitPasswordLoading={submitPasswordLoading}
        saveImage={saveImage}
        image={image}
        defaultPickerValue={defaultPickerValue}
        disabledDate={disabledDate}
        handleBack={handleBack}
        checkMatchingPasswords={checkMatchingPasswords}
        checkSamePasswords={checkSamePasswords}
        samePasswords={samePasswords}
        matchingPasswords={matchingPasswords}
        loading={loading}
        title={title}
        formHasErrors={formHasErrors}
        handleBlurName={handleBlurName}
        imgLoading={imgLoading}
      />
    </>
  );
};

export default ManageUserProfilePage;
